import { PaperClipOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { Tag,Tooltip } from "../../../../../../../shared/frontend/ui";
import {
COMMUNICATION_STATUS_FAILED,
COMMUNICATION_STATUS_PENDING,
COMMUNICATION_STATUS_SENDING,
COMMUNICATION_STATUS_SENT
} from "../../../app/constants/communicationQueueStatus";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateTimeNoSeconds } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";

const PERSON_DIGITAL_FILE_TIMELINE_UTILS_COLUMN_WIDTH = {
  occurredAt: 170,
  direction: 110,
  type: 120,
  status: 120,
  hasAttachment: 110
} as const;

export type PersonDigitalFileTimelineRow = {
  id: string;
  source: "document" | "email" | "reminder";
  direction: "in" | "out";
  type: "letter" | "email" | "reminder" | "contract" | "upload" | "other";
  occurredAt: string;
  title?: string | null;
  summary?: string | null;
  status?: string | null;
  hasAttachment: boolean;
  attachmentDocumentId?: string | null;
  contractPackageId?: string | null;
  reminderId?: string | null;
  emailQueueId?: string | null;
  personDocumentId?: string | null;
};

export const PERSON_DIGITAL_FILE_TIMELINE_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "occurredAt", label: "Date", defaultVisible: true },
  { key: "direction", label: "Direction", defaultVisible: true },
  { key: "type", label: "Type", defaultVisible: true },
  { key: "title", label: "Title", defaultVisible: true },
  { key: "status", label: "Status", defaultVisible: true },
  { key: "hasAttachment", label: "Attachment", defaultVisible: true }
];

/** Backend-Status eines Dokuments, zu dem keine Datei im Speicher liegt (Altdaten-Import). */
export const PERSON_DOCUMENT_TIMELINE_STATUS_NO_FILE = "nofile";

export function renderDigitalFileTimelineStatusTag(status: string | null | undefined, text: I18nContextValue["text"]) {
  const normalized = String(status || "").toLowerCase();
  if (!normalized) return null;
  if (normalized === PERSON_DOCUMENT_TIMELINE_STATUS_NO_FILE) return <Tag>{text("No file")}</Tag>;
  if (normalized === COMMUNICATION_STATUS_SENT) return <Tag color="green">{text("Sent")}</Tag>;
  if (normalized === COMMUNICATION_STATUS_PENDING) return <Tag color="orange">{text("Pending")}</Tag>;
  if (normalized === COMMUNICATION_STATUS_FAILED) return <Tag color="red">{text("Failed")}</Tag>;
  if (normalized === COMMUNICATION_STATUS_SENDING) return <Tag color="blue">{text("Sending")}</Tag>;
  return <Tag>{status}</Tag>;
}

export function renderDigitalFileTimelineTypeTag(type: PersonDigitalFileTimelineRow["type"], text: I18nContextValue["text"]) {
  if (type === "letter") return <Tag color="geekblue">{text("Letter")}</Tag>;
  if (type === "email") return <Tag color="cyan">{text("Email")}</Tag>;
  if (type === "reminder") return <Tag color="orange">{text("Reminder")}</Tag>;
  if (type === "contract") return <Tag color="purple">{text("Contract")}</Tag>;
  if (type === "upload") return <Tag color="blue">{text("File")}</Tag>;
  return <Tag>{text("Other")}</Tag>;
}

export function createDigitalFileTimelineColumns(
  text: I18nContextValue["text"],
  units: UnitsSettings
): Record<string, ListColumn<PersonDigitalFileTimelineRow>> {
  return {
    occurredAt: {
      title: text("Date"),
      dataIndex: "occurredAt",
      key: "occurredAt",
      width: PERSON_DIGITAL_FILE_TIMELINE_UTILS_COLUMN_WIDTH.occurredAt,
      sorter: true,
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => formatDateTimeNoSeconds(String(row.occurredAt || ""), units, { omitTimeWhenMidnight: true })
    },
    direction: {
      title: text("Direction"),
      dataIndex: "direction",
      key: "direction",
      width: PERSON_DIGITAL_FILE_TIMELINE_UTILS_COLUMN_WIDTH.direction,
      sorter: true,
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => row.direction === "in"
        ? <Tag color="blue">{text("Incoming")}</Tag>
        : <Tag color="green">{text("Outgoing")}</Tag>
    },
    type: {
      title: text("Type"),
      dataIndex: "type",
      key: "type",
      width: PERSON_DIGITAL_FILE_TIMELINE_UTILS_COLUMN_WIDTH.type,
      sorter: true,
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => renderDigitalFileTimelineTypeTag(row.type, text)
    },
    title: {
      title: text("Title"),
      dataIndex: "title",
      key: "title",
      ellipsis: false,
      sorter: true,
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => row.title || text("(no title)")
    },
    status: {
      title: text("Status"),
      dataIndex: "status",
      key: "status",
      width: PERSON_DIGITAL_FILE_TIMELINE_UTILS_COLUMN_WIDTH.status,
      sorter: true,
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => renderDigitalFileTimelineStatusTag(row.status, text) ?? "\u2014"
    },
    hasAttachment: {
      title: text("Attachment"),
      dataIndex: "hasAttachment",
      key: "hasAttachment",
      width: PERSON_DIGITAL_FILE_TIMELINE_UTILS_COLUMN_WIDTH.hasAttachment,
      align: "center",
      sorter: true,
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => row.hasAttachment
        ? <Tooltip title={text("Attachment available")}><PaperClipOutlined /></Tooltip>
        : null
    }
  };
}
